/**
 * Reveal math — when do matches open for a given event.
 *
 * Reveal time = event_date - match_reveal_hours hours. Before that, mutual
 * matches exist in the database but contacts stay hidden; the UI shows
 * aggregate counts + countdown instead of names.
 */

export interface RevealEvent {
  event_date: string;
  match_reveal_hours: number;
  /**
   * Organizer override — when true, the event is "always revealed": contacts
   * and the AfterReveal UI are shown regardless of how far away event_date
   * is. The server-side find_my_meetup_matches RPC already honours this
   * flag; this field exists so the client conditional that picks between
   * BeforeReveal and AfterReveal stays in sync with the server.
   */
  reveal_immediately?: boolean | null;
}

export function revealAt(event: RevealEvent): Date {
  return new Date(
    new Date(event.event_date).getTime() - event.match_reveal_hours * 3_600_000,
  );
}

export function isRevealed(event: RevealEvent, now: Date = new Date()): boolean {
  if (event.reveal_immediately) return true;
  return now.getTime() >= revealAt(event).getTime();
}

export function msUntilReveal(event: RevealEvent, now: Date = new Date()): number {
  if (event.reveal_immediately) return 0;
  return Math.max(0, revealAt(event).getTime() - now.getTime());
}

export function formatCountdown(ms: number): string {
  if (ms <= 0) return "00:00:00";
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const mins = Math.floor((total % 3_600) / 60);
  const secs = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return days > 0
    ? `${days}d ${pad(hours)}:${pad(mins)}:${pad(secs)}`
    : `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}
